(function () {
  'use strict';

  const RoomFit = typeof module !== 'undefined' && module.exports ? require('./jc-roomfit.js') : window.JCRoomFit;
  const STORAGE_KEY = 'jc_wellness_match_session';
  const WEIGHTS = { goals: 20, bodyAreas: 25, lifestyles: 10, intensity: 10, features: 15, height: 8, room: 7, budget: 5 };
  const LABELS = {
    goals: { relaxation: 'relaxation', after_work: 'after-work recovery', post_workout: 'post-workout recovery', neck_shoulders: 'neck and shoulder comfort', back_lumbar: 'back and lumbar comfort', legs_feet: 'leg and foot recovery', full_body: 'full-body massage', luxury: 'a luxury experience' },
    features: { heat: 'heat', foot_massage: 'foot massage', zero_gravity: 'Zero Gravity', voice_control: 'voice control', ai_body_scan: 'AI body scan', stretching: 'advanced stretching' }
  };

  function overlap(selected, supported) {
    if (!Array.isArray(supported) || !selected.length) return 0;
    return selected.filter(value => supported.includes(value)).length / selected.length;
  }

  function inferCatalogTags(product) {
    if (Array.isArray(product.goals) && product.goals.length &&
      Array.isArray(product.bodyAreas) && product.bodyAreas.length) return product;
    const text = typeof product.catalogDescription === 'string' ? product.catalogDescription.toLowerCase() : '';
    const bodyAreas = [];
    if (/\bneck\b/.test(text)) bodyAreas.push('neck');
    if (/\bshoulder(s)?\b/.test(text)) bodyAreas.push('shoulders');
    if (/\bupper[\s-]+back\b/.test(text)) bodyAreas.push('upper_back');
    if (/\b(lower[\s-]+back|lumbar)\b/.test(text)) bodyAreas.push('lower_back');
    if (/\b(hip(s)?|thigh(s)?)\b/.test(text)) bodyAreas.push('hips');
    if (/\b(arm(s)?|hand(s)?)\b/.test(text)) bodyAreas.push('arms_hands');
    if (/\b(calf|calves)\b/.test(text)) bodyAreas.push('calves');
    if (/\b(foot|feet|sole(s)?)\b/.test(text)) bodyAreas.push('feet');
    const goals = [];
    if (/\b(relax(ation|ing)?|stress[\s-]+relief)\b/.test(text)) goals.push('relaxation');
    if (/\b(after[\s-]+work|desk[\s-]+work|office[\s-]+work)\b/.test(text)) goals.push('after_work');
    if (/\b(post[\s-]+workout|post[\s-]+exercise|sports?[\s-]+recovery)\b/.test(text)) goals.push('post_workout');
    if (bodyAreas.includes('neck') || bodyAreas.includes('shoulders')) goals.push('neck_shoulders');
    if (bodyAreas.includes('upper_back') || bodyAreas.includes('lower_back')) goals.push('back_lumbar');
    if (bodyAreas.includes('calves') || bodyAreas.includes('feet')) goals.push('legs_feet');
    if (/\bfull[\s-]+body\b/.test(text)) goals.push('full_body');
    if (/\b(luxury|premium)\b/.test(text)) goals.push('luxury');
    const features = [];
    if (/\b(heat(ing|ed)?|thermotherapy)\b/.test(text)) features.push('heat');
    if (/\b(foot|feet)[\s-]+massage\b/.test(text)) features.push('foot_massage');
    if (/\bzero[\s-]+gravity\b/.test(text)) features.push('zero_gravity');
    if (/\bvoice[\s-]+control\b/.test(text)) features.push('voice_control');
    if (/\b(ai[\s-]+body[\s-]+scan|body[\s-]+scanning)\b/.test(text)) features.push('ai_body_scan');
    if (/\bstretch(ing)?\b/.test(text)) features.push('stretching');
    return {
      ...product,
      goals: Array.isArray(product.goals) && product.goals.length ? product.goals : goals,
      bodyAreas: Array.isArray(product.bodyAreas) && product.bodyAreas.length ? product.bodyAreas : bodyAreas,
      features: Array.isArray(product.features) && product.features.length ? product.features : features,
      inferred: true
    };
  }

  function scoreProduct(product, answers) {
    const scores = {};
    let earned = 0;
    let possible = 0;
    function add(name, fraction) {
      if (fraction === null) return;
      scores[name] = Math.max(0, Math.min(1, fraction)) * WEIGHTS[name];
      earned += scores[name];
      possible += WEIGHTS[name];
    }
    add('goals', answers.goals.length ? overlap(answers.goals, product.goals) : null);
    add('bodyAreas', answers.bodyAreas.length ? overlap(answers.bodyAreas, product.bodyAreas) : null);
    add('lifestyles', answers.lifestyles.length ? overlap(answers.lifestyles, product.lifestyles) : null);
    add('intensity', answers.intensity && Array.isArray(product.intensities) ? Number(product.intensities.includes(answers.intensity)) : null);
    add('features', answers.features.length ? overlap(answers.features, product.features) : null);

    const hasHeight = Number.isFinite(product.heightMin) && Number.isFinite(product.heightMax) && product.heightMin <= product.heightMax;
    const heightGap = hasHeight && Number.isFinite(answers.height) ? Math.max(product.heightMin - answers.height, answers.height - product.heightMax, 0) : null;
    add('height', heightGap === null ? null : heightGap === 0 ? 1 : heightGap <= 5 ? 0.5 : 0);

    const measured = Number.isFinite(answers.width) && Number.isFinite(answers.depth);
    const room = measured && RoomFit ? RoomFit.evaluate(product.dimensions, answers.width, answers.depth) : null;
    const fits = room && room.status === 'great';
    add('room', !room || room.status === 'unknown' ? null : fits ? 1 : room.status === 'tight' ? 3 / 7 : 0);

    const price = product.price / 100;
    const limit = { under_3000: 3000, from_3000_5000: 5000, from_5000_8000: 8000, from_8000_15000: 15000 }[answers.budget];
    add('budget', !answers.budget || answers.budget === 'flexible' || !Number.isFinite(price) || price < 0 ? null :
      answers.budget === 'over_15000' ? Number(price >= 15000) :
      price <= limit ? 1 : price <= limit * 1.15 ? 0.4 : 0);

    const reasons = [];
    const matchedGoals = answers.goals.filter(value => (product.goals || []).includes(value));
    if (matchedGoals.length) reasons.push('Supports your goal of ' + LABELS.goals[matchedGoals[0]]);
    if (scores.bodyAreas > 0) reasons.push('Covers ' + Math.round(scores.bodyAreas / WEIGHTS.bodyAreas * answers.bodyAreas.length) + ' of your selected massage areas');
    if (scores.intensity === WEIGHTS.intensity) reasons.push('Offers your preferred ' + answers.intensity + ' intensity');
    answers.features.filter(value => (product.features || []).includes(value)).slice(0, 2).forEach(value => reasons.push('Includes ' + LABELS.features[value]));
    if (heightGap === 0) reasons.push('Within the listed height range');
    if (fits) reasons.push('Fits your entered room dimensions');
    if (scores.budget === WEIGHTS.budget) reasons.push('Within your selected budget');

    return {
      product,
      score: possible ? Math.round(earned / possible * 100) : 0,
      scores,
      reasons,
      heightStatus: !Number.isFinite(answers.height) ? 'Height not provided — fit can be confirmed with our team' : !hasHeight ? 'Height fit information unavailable' : heightGap === 0 ? 'Height compatible' : 'Check height fit',
      roomStatus: !measured ? 'Room dimensions not entered' : !room || room.status === 'unknown' ? 'Space check recommended — chair dimensions unavailable' : fits ? 'Fits your space' : 'Space check recommended'
    };
  }

  function rankProducts(products, answers) {
    return products.filter(product => product.available && Array.isArray(product.goals) && Array.isArray(product.bodyAreas))
      .map(product => scoreProduct(product, answers))
      .sort((a, b) => b.score - a.score ||
        (b.scores.bodyAreas || 0) - (a.scores.bodyAreas || 0) ||
        (b.scores.features || 0) - (a.scores.features || 0) ||
        (b.scores.goals || 0) - (a.scores.goals || 0) ||
        (b.product.priority || 0) - (a.product.priority || 0) ||
        a.product.price - b.product.price ||
        a.product.id - b.product.id);
  }

  function resultTiers(ranked) {
    if (!ranked.length) return [];
    const tiers = [{ ...ranked[0], tier: 'Best Match' }];
    if (ranked.length > 1) tiers.push({ ...ranked[1], tier: 'Great Alternative' });
    const upgrade = ranked.slice(2).find(item =>
      item.product.price > ranked[0].product.price &&
      item.score >= ranked[0].score * 0.8 &&
      (item.scores.features || 0) >= (ranked[0].scores.features || 0));
    if (upgrade) tiers.push({ ...upgrade, tier: 'Premium Upgrade' });
    return tiers;
  }

  const Engine = { inferCatalogTags, scoreProduct, rankProducts, resultTiers };
  if (typeof module !== 'undefined' && module.exports) module.exports = Engine;
  if (typeof document === 'undefined') return;

  function init(root) {
    if (root.dataset.jcInitialized) return;
    root.dataset.jcInitialized = 'true';
    const steps = Array.from(root.querySelectorAll('[data-jc-step]'));
    const status = root.querySelector('[data-jc-status]');
    const results = root.querySelector('[data-jc-results]');
    const error = root.querySelector('[data-jc-error]');
    const productsNode = root.querySelector('[data-jc-products]');
    if (!productsNode) return;
    let products;
    try {
      products = JSON.parse(productsNode.textContent)
        .filter(product => !/\bvending\b/i.test(product.title))
        .map(inferCatalogTags);
    } catch (cause) {
      console.error('JC Wellness Match: product data could not be read', cause);
      error.textContent = 'We could not load chair information. Please try again later.';
      error.hidden = false;
      return;
    }
    if (!products.length) return;
    if (!products.some(product => product.available && product.goals.length && product.bodyAreas.length)) {
      console.error('JC Wellness Match: selected collection has no chairs with confirmed matching information');
      error.textContent = 'Chair descriptions do not yet contain enough information for a recommendation. Please ask a JC wellness advisor.';
      error.hidden = false;
      root.querySelector('[data-jc-quiz]').hidden = true;
      return;
    }

    const defaults = () => ({ goals: [], bodyAreas: [], lifestyles: [], intensity: '', features: [], height: null, space: '', width: null, depth: null, budget: '' });
    let answers = defaults();
    let step = 0;
    let completed = false;
    let started = false;
    try {
      const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
      if (saved && saved.version === 1 && saved.answers &&
        ['goals', 'bodyAreas', 'lifestyles', 'features'].every(key => Array.isArray(saved.answers[key])) &&
        Number.isInteger(saved.step) && saved.step >= 0 && saved.step < steps.length) {
        answers = { ...answers, ...saved.answers };
        step = saved.step;
        completed = Boolean(saved.completed);
        started = true;
      }
    } catch (cause) {
      console.error('JC Wellness Match: saved answers could not be restored', cause);
      status.textContent = 'Your previous answers could not be restored. You can start again below.';
    }

    function persist() {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ version: 1, answers, step, completed }));
      } catch (cause) {
        console.error('JC Wellness Match: answers could not be saved for refresh', cause);
        status.textContent = 'Your answers will remain until this page is closed, but refresh recovery is unavailable.';
      }
    }
    function restore() {
      root.querySelectorAll('input[data-jc-field]').forEach(input => {
        const value = answers[input.dataset.jcField];
        if (input.type === 'checkbox') input.checked = Array.isArray(value) && value.includes(input.value);
        else if (input.type === 'radio') input.checked = value === input.value;
        else input.value = value === null ? '' : value;
      });
      updateMap();
    }
    function updateMap() {
      root.querySelectorAll('[data-jc-area]').forEach(node => {
        const selected = answers.bodyAreas.includes(node.dataset.jcArea);
        node.setAttribute('aria-pressed', String(selected));
        node.classList.toggle('is-selected', selected);
      });
    }
    function showStep() {
      results.hidden = true;
      root.querySelector('[data-jc-quiz]').hidden = false;
      steps.forEach((panel, index) => { panel.hidden = index !== step; });
      root.querySelector('[data-jc-current]').textContent = String(step + 1);
      root.querySelector('[data-jc-progress]').style.width = ((step + 1) / steps.length * 100) + '%';
      root.querySelector('[data-jc-back]').disabled = step === 0;
      root.querySelector('[data-jc-next]').textContent = step === steps.length - 1 ? 'Find my match' : 'Continue';
      root.querySelector('[data-jc-skip]').textContent = step === steps.length - 1 ? 'Skip & explore chairs' : 'Skip this step';
      root.querySelectorAll('[data-jc-progress-step]').forEach((node, index) => {
        node.setAttribute('aria-current', index === step ? 'step' : 'false');
        node.classList.toggle('is-done', index < step);
      });
      error.hidden = true;
      persist();
    }
    function validate() {
      const messages = [
        answers.goals.length && answers.goals.length <= 3 ? '' : 'Select one to three wellness goals.',
        answers.bodyAreas.length ? '' : 'Select at least one massage area.',
        answers.lifestyles.length ? '' : 'Select at least one lifestyle option.',
        answers.intensity ? '' : 'Choose your preferred massage intensity.',
        answers.height === null || (Number.isInteger(answers.height) && answers.height >= 120 && answers.height <= 220) ? '' : 'Enter a height between 120 and 220 cm, or leave it blank.',
        !answers.space ? 'Select where the chair will live.' : (answers.width === null && answers.depth === null) || (Number.isFinite(answers.width) && Number.isFinite(answers.depth) && answers.width >= 50 && answers.width <= 500 && answers.depth >= 50 && answers.depth <= 500) ? '' : 'Enter both room dimensions between 50 and 500 cm, or leave both blank.',
        answers.budget ? '' : 'Choose a budget or select Flexible.'
      ];
      return messages[step];
    }
    function money(cents) {
      return new Intl.NumberFormat('en-AE', { style: 'currency', currency: root.dataset.jcCurrency, maximumFractionDigits: 2 }).format(cents / 100);
    }
    function el(tag, className, text) {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text !== undefined) node.textContent = text;
      return node;
    }
    function link(label, url, className) {
      const node = el('a', className, label);
      node.href = url;
      return node;
    }
    function resultContext(item) {
      return {
        productTitle: item.product.title, productHandle: item.product.handle,
        score: item.score, goals: answers.goals.slice(), bodyAreas: answers.bodyAreas.slice(),
        lifestyle: answers.lifestyles.slice(), intensity: answers.intensity, features: answers.features.slice(),
        height: answers.height, room: { space: answers.space, width: answers.width, depth: answers.depth },
        budget: answers.budget
      };
    }
    function openJCWellnessAI(item) {
      const detail = resultContext(item);
      document.dispatchEvent(new CustomEvent('jc:wellness-ai-requested', { detail }));
      const launcher = document.querySelector('#jcbAsst [data-jcba-toggle]');
      if (launcher) launcher.click();
      else status.textContent = 'The JC assistant is not available on this page right now.';
    }
    function card(item) {
      const { product } = item;
      const article = el('article', 'jcwm-product');
      const imageLink = link('', product.url, 'jcwm-product__image');
      if (product.image) {
        const image = el('img');
        image.src = product.image;
        image.alt = product.title;
        image.loading = 'lazy';
        image.width = 600;
        imageLink.append(image);
      }
      imageLink.addEventListener('click', () => document.dispatchEvent(new CustomEvent('jc_wellness_product_viewed', { detail: { matched_product_handle: product.handle, match_score: item.score } })));
      article.append(imageLink, el('span', 'jcwm-eyebrow', item.tier), el('h3', 'jcwm-product__title', product.title));
      if (item.score !== null) article.append(el('p', 'jcwm-product__score', item.score + '% Match'));
      const price = el('p', 'jcwm-product__price', money(product.price));
      if (product.compareAtPrice > product.price) price.append(el('s', '', money(product.compareAtPrice)));
      article.append(price, el('p', 'jcwm-product__stock', 'Available'));
      if (product.description) article.append(el('p', 'jcwm-product__description', product.description));
      if (product.inferred) article.append(el('p', 'jcwm-fit', 'Demo match based on listed product details. Please confirm features and fit with our team.'));
      if (item.reasons.length) {
        article.append(el('h4', '', 'Why it matches you'));
        const list = el('ul', 'jcwm-reasons');
        item.reasons.forEach(reason => list.append(el('li', '', reason)));
        article.append(list);
      }
      article.append(el('p', 'jcwm-fit', item.heightStatus), el('p', 'jcwm-fit', item.roomStatus));
      article.append(el('p', 'jcwm-fit',
        Number.isInteger(product.maxUserWeightKg) && product.maxUserWeightKg > 0
          ? 'This chair is rated for up to ' + product.maxUserWeightKg + ' kg. Our team is happy to help you confirm a comfortable fit.'
          : 'For your comfort, our team can confirm the suitable weight capacity for this chair.'));
      const actions = el('div', 'jcwm-actions');
      if (root.dataset.jcDemo === 'true') {
        const demoButton = el('button', 'jcwm-button jcwm-button--primary', 'Add to Cart');
        demoButton.type = 'button';
        demoButton.disabled = true;
        actions.append(demoButton, el('p', 'jcwm-cart-note', 'Demo chair — not available for purchase.'));
      } else {
        const variants = Array.isArray(product.variants) ? product.variants.filter(variant =>
          variant && /^[1-9]\d*$/.test(String(variant.id)) && Number.isFinite(variant.price) && variant.price >= 0) : [];
        if (variants.length && root.dataset.jcCartAddUrl) {
          const form = el('form', 'jcwm-cart-form');
          form.method = 'post';
          form.action = root.dataset.jcCartAddUrl;
          if (variants.length > 1) {
            const label = el('label', 'jcwm-variant-label', 'Choose your chair option');
            const select = el('select', 'jcwm-variant-select');
            select.name = 'id';
            variants.forEach(variant => {
              const option = el('option', '', variant.title + ' — ' + money(variant.price));
              option.value = String(variant.id);
              select.append(option);
            });
            select.addEventListener('change', () => {
              const variant = variants.find(option => String(option.id) === select.value);
              if (!variant) {
                console.error('JC Wellness Match: selected chair option is unavailable');
                status.textContent = 'This chair option is unavailable. Please select another option.';
                form.querySelector('button').disabled = true;
                return;
              }
              form.querySelector('button').disabled = false;
              if (status.textContent === 'This chair option is unavailable. Please select another option.') status.textContent = '';
              price.replaceChildren(document.createTextNode(money(variant.price)));
              if (variant.compareAtPrice > variant.price) price.append(el('s', '', money(variant.compareAtPrice)));
            });
            label.append(select);
            form.append(label);
            price.replaceChildren(document.createTextNode(money(variants[0].price)));
            if (variants[0].compareAtPrice > variants[0].price) price.append(el('s', '', money(variants[0].compareAtPrice)));
          } else {
            const variantId = el('input');
            variantId.type = 'hidden';
            variantId.name = 'id';
            variantId.value = String(variants[0].id);
            form.append(variantId);
          }
          const quantity = el('input');
          quantity.type = 'hidden';
          quantity.name = 'quantity';
          quantity.value = '1';
          const submit = el('button', 'jcwm-button jcwm-button--primary', 'Add to Cart');
          submit.type = 'submit';
          form.append(quantity, submit);
          actions.append(form);
        } else {
          console.error('JC Wellness Match: available chair has no valid cart variant or cart URL', product.handle);
          const details = link('View Product Details', product.url, 'jcwm-button jcwm-button--primary');
          details.addEventListener('click', () => document.dispatchEvent(new CustomEvent('jc_wellness_product_viewed', { detail: { matched_product_handle: product.handle, match_score: item.score } })));
          actions.append(details, el('p', 'jcwm-cart-note', 'Direct cart checkout is unavailable for this chair.'));
        }
      }
      if (root.dataset.jcTrialUrl) {
        const trial = link('Book a Home Trial', root.dataset.jcTrialUrl, 'jcwm-button jcwm-button--light');
        trial.addEventListener('click', () => document.dispatchEvent(new CustomEvent('jc_wellness_trial_clicked')));
        actions.append(trial);
      }
      if (root.dataset.jcShowAi === 'true') {
        const ai = el('button', 'jcwm-button jcwm-button--light', root.dataset.jcAiLabel);
        ai.type = 'button';
        ai.addEventListener('click', () => {
          openJCWellnessAI(item);
          document.dispatchEvent(new CustomEvent('jc_wellness_ai_clicked'));
        });
        actions.append(ai);
      }
      if (root.dataset.jcShowWhatsapp === 'true' && /^\d{8,15}$/.test(root.dataset.jcWhatsapp)) {
        const message = ['Hello JC Buckman,', 'I explored the JC Wellness Match.', 'Chair: ' + product.title,
          ...(item.score === null ? [] : ['Match: ' + item.score + '%']),
          'I would like more information about this chair.', new URL(product.url, location.origin).href].join('\n\n');
        const whatsapp = link('WhatsApp a Wellness Advisor', 'https://wa.me/' + root.dataset.jcWhatsapp + '?text=' + encodeURIComponent(message), 'jcwm-button jcwm-button--light');
        whatsapp.rel = 'noopener noreferrer';
        whatsapp.target = '_blank';
        whatsapp.addEventListener('click', () => document.dispatchEvent(new CustomEvent('jc_wellness_whatsapp_clicked')));
        actions.append(whatsapp);
      }
      article.append(actions);
      return article;
    }
    function showResults() {
      const ranked = rankProducts(products, answers);
      const personalized = Boolean(answers.goals.length || answers.bodyAreas.length || answers.lifestyles.length ||
        answers.intensity || answers.features.length || Number.isFinite(answers.height) ||
        (Number.isFinite(answers.width) && Number.isFinite(answers.depth)) ||
        (answers.budget && answers.budget !== 'flexible'));
      const selected = personalized ? resultTiers(ranked) : ranked.slice(0, 3).map((item, index) =>
        ({ ...item, score: null, tier: index === 0 ? 'Explore Our Chairs' : 'More Chairs' }));
      root.querySelector('[data-jc-results-label]').textContent = personalized ? 'CURATED FOR YOU' : 'EXPLORE OUR CHAIRS';
      root.querySelector('[data-jc-results-heading]').textContent = personalized ? 'Your Wellness Match' : 'Discover JC Buckman Chairs';
      root.querySelector('[data-jc-results-note]').textContent = personalized
        ? 'Based on the preferences you shared. You can start over to add or change your answers.'
        : 'You skipped the fit questions, so these are chairs to explore, not personalized matches. Answer a few questions anytime for tailored suggestions.';
      const cards = root.querySelector('[data-jc-cards]');
      cards.replaceChildren();
      selected.forEach(item => cards.append(card(item)));
      root.querySelector('[data-jc-quiz]').hidden = true;
      results.hidden = false;
      if (selected.length) {
        const context = resultContext(selected[0]);
        window.JC_WELLNESS_MATCH = context;
        try {
          sessionStorage.setItem('jc_wellness_match_result', JSON.stringify(context));
        } catch (cause) {
          console.error('JC Wellness Match: result could not be saved for this session', cause);
        }
        document.dispatchEvent(new CustomEvent('jc:wellness-match', { detail: context }));
        document.dispatchEvent(new CustomEvent('jc_wellness_completed', { detail: { matched_product_handle: context.productHandle, match_score: context.score } }));
      } else status.textContent = 'No available chairs have matching data yet. Please contact a JC wellness advisor.';
      completed = true;
      persist();
    }

    root.addEventListener('change', event => {
      const input = event.target.closest('input[data-jc-field]');
      if (!input || !root.contains(input)) return;
      const field = input.dataset.jcField;
      if (input.type === 'checkbox') {
        const values = answers[field];
        if (input.checked && field === 'goals' && values.length >= 3) {
          input.checked = false;
          error.textContent = 'Select up to three wellness goals.';
          error.hidden = false;
          return;
        }
        answers[field] = input.checked ? [...values, input.value] : values.filter(value => value !== input.value);
      } else if (input.type === 'number') {
        answers[field] = input.value.trim() === '' ? null : Number(input.value);
      } else answers[field] = input.value;
      if (!started) {
        started = true;
        document.dispatchEvent(new CustomEvent('jc_wellness_started'));
      }
      updateMap();
      error.hidden = true;
      persist();
    });
    root.querySelectorAll('[data-jc-area]').forEach(node => {
      function toggle() {
        const checkbox = root.querySelector('input[data-jc-field="bodyAreas"][value="' + node.dataset.jcArea + '"]');
        checkbox.checked = !checkbox.checked;
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      }
      node.addEventListener('click', toggle);
      node.addEventListener('keydown', event => {
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); toggle(); }
      });
    });
    root.querySelector('[data-jc-back]').addEventListener('click', () => { step--; showStep(); steps[step].querySelector('h2').focus(); });
    function advance(skip) {
      const message = skip ? '' : validate();
      if (message) {
        error.textContent = message;
        error.hidden = false;
        return;
      }
      if (skip) {
        const fields = [
          ['goals'], ['bodyAreas'], ['lifestyles'], ['intensity', 'features'],
          ['height'], ['space', 'width', 'depth'], ['budget']
        ][step];
        const empty = defaults();
        fields.forEach(field => { answers[field] = empty[field]; });
        restore();
        document.dispatchEvent(new CustomEvent('jc_wellness_step_skipped', { detail: { step_number: step + 1 } }));
      } else document.dispatchEvent(new CustomEvent('jc_wellness_step_completed', { detail: { step_number: step + 1 } }));
      error.hidden = true;
      if (step === steps.length - 1) {
        status.textContent = 'Finding your Wellness Match...';
        root.querySelector('[data-jc-next]').disabled = true;
        root.querySelector('[data-jc-skip]').disabled = true;
        setTimeout(() => {
          root.querySelector('[data-jc-next]').disabled = false;
          root.querySelector('[data-jc-skip]').disabled = false;
          status.textContent = '';
          showResults();
          results.querySelector('h2').focus();
        }, 700);
      } else { step++; showStep(); steps[step].querySelector('h2').focus(); }
    }
    root.querySelector('[data-jc-next]').addEventListener('click', () => advance(false));
    root.querySelector('[data-jc-skip]').addEventListener('click', () => advance(true));
    root.querySelectorAll('[data-jc-reset]').forEach(button => button.addEventListener('click', () => {
      answers = defaults(); step = 0; completed = false; started = false;
      try { sessionStorage.removeItem(STORAGE_KEY); sessionStorage.removeItem('jc_wellness_match_result'); }
      catch (cause) { console.error('JC Wellness Match: session could not be cleared', cause); }
      delete window.JC_WELLNESS_MATCH;
      status.textContent = ''; restore(); showStep();
      steps[0].querySelector('h2').focus();
    }));
    restore();
    if (completed) showResults();
    else showStep();
  }

  function boot(container) {
    container.querySelectorAll('[data-jc-wellness-match]').forEach(init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(document));
  else boot(document);
  document.addEventListener('shopify:section:load', event => boot(event.target));
}());
